import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as PublishIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { PostPreview } from './PostPreview';
import { DetailPost } from './PostDetailDialog';
import { useIsMobile } from '../hooks/useIsMobile';

interface Props {
  post: DetailPost | null;
  /** Where each platform would publish, from GET /api/platforms. */
  targets?: Record<string, string | null>;
  /** When the post goes out, already formatted. Omitted when it is not scheduled. */
  when?: string;
  busy?: boolean;
  /** Actions are omitted entirely when the caller does not supply a handler. */
  onPublish?: (post: DetailPost) => void;
  onEdit?: (post: DetailPost) => void;
  onClose: () => void;
}

/**
 * How a post will read on each network, on its own, before it goes out.
 *
 * The same `PostPreview` is already the last section of `PostDetailDialog` — below the
 * per-platform outcomes, the engagement figures and the full text. That is the right order
 * for a post that has published and the wrong one for a post that has not, because those
 * three sections are empty or inapplicable until it does. Checking a scheduled post meant
 * opening the record and scrolling past all of them to reach the only part that applied.
 *
 * So this is a second door onto the same component, not a second preview. It opens on the
 * thing being checked and offers the two actions that follow from checking it: fix it, or
 * send it. Keep it rendering `PostPreview` rather than growing its own copy, or the two
 * will drift on character limits and the preview will stop matching what publishes.
 */
export const PostPreviewDialog: React.FC<Props> = ({
  post,
  targets = {},
  when,
  busy = false,
  onPublish,
  onEdit,
  onClose,
}) => {
  const isMobile = useIsMobile();
  if (!post) return null;

  return (
    <Dialog open fullScreen={isMobile} fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="div">Preview</Typography>
          {/* Only stated when it is actually scheduled — a draft has no date to promise. */}
          {when && (
            <Typography variant="caption" color="text.secondary">
              Goes out {when}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ ml: 'auto' }} aria-label="Close preview">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          An approximation for checking length and layout, not a pixel-accurate mock.
        </Typography>

        <PostPreview
          content={post.content}
          contentVariants={post.content_variants}
          platforms={post.platforms}
          mediaUrls={post.media_urls}
          results={post.results as never}
          targets={targets}
        />
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        <Box sx={{ flexGrow: 1 }} />
        {onEdit && (
          <Button startIcon={<EditIcon />} disabled={busy} onClick={() => onEdit(post)}>
            Edit
          </Button>
        )}
        {onPublish && (
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            disabled={busy}
            onClick={() => onPublish(post)}
          >
            Publish now
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
